import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import axios from "axios";
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Input,
  Spinner,
  Stack,
  Tag,
  Text,
  useToast,
} from "@chakra-ui/react";
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiArrowLeft,
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiFile,
  FiFileText,
  FiInfo,
  FiMapPin,
  FiPackage,
  FiTruck,
  FiUploadCloud,
  FiZap,
} from "react-icons/fi";
import WithSubnavigation from "./navbar";
import { getScannedPrescriptionFile, clearScannedPrescriptionFile, getPendingSavedPrescriptions, clearPendingSavedPrescriptions } from "../../prescriptionStore";
import { API_BASE } from "../../config";
import { resolveFileUrl } from "../../utils/fileUrl";
import { performLogout } from "../../utils/logout";

const hasCookie = (name) => {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split(";")
    .some((c) => c.trim().startsWith(`${name}=`));
};

const API = API_BASE;

const RAZORPAY_SCRIPT_ID = "razorpay-checkout-script";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const existing = document.getElementById(RAZORPAY_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(true), { once: true });
      existing.addEventListener("error", () => resolve(false), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.id = RAZORPAY_SCRIPT_ID;
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const formatCurrency = (v) => `₹${Number(v || 0).toFixed(2)}`;
const formatCurrencyPrecise = (v) => `₹${Number(v || 0).toFixed(2)}`;
const formatDistance = (v) => `${Number(v || 0).toFixed(1)} km`;

const colors = {
  brand: { 50: "#f0fdfa", 500: "#0d9488", 600: "#0f766e" },
  neutral: { 50: "#f8fafc", 100: "#f1f5f9", 200: "#e2e8f0", 400: "#94a3b8", 500: "#64748b", 700: "#334155", 800: "#1e293b", 900: "#0f172a" },
};

const SectionCard = ({ children, ...props }) => (
  <Box
    bg="white"
    borderRadius="16px"
    border="1px solid"
    borderColor={colors.neutral[200]}
    boxShadow="0 1px 4px rgba(15,23,42,0.05)"
    overflow="hidden"
    {...props}
  >
    {children}
  </Box>
);

const SectionHeader = ({ icon, title, subtitle }) => (
  <Flex align="center" gap={3} px={5} py={4} borderBottom="1px solid" borderColor={colors.neutral[100]}>
    <Box w="34px" h="34px" borderRadius="10px" bg={colors.brand[50]} display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
      <Icon as={icon} color={colors.brand[600]} boxSize={4} />
    </Box>
    <Box>
      <Text fontWeight="800" fontSize="14px" color={colors.neutral[900]} m={0}>{title}</Text>
      {subtitle && <Text fontSize="12px" color={colors.neutral[500]} m={0}>{subtitle}</Text>}
    </Box>
  </Flex>
);

const DeliveryOptionCard = ({ option, isSelected, onSelect }) => (
  <Box
    as="button"
    w="full"
    textAlign="left"
    onClick={() => onSelect(option)}
    px={4}
    py={3}
    borderRadius="12px"
    border="1.5px solid"
    borderColor={isSelected ? colors.brand[600] : colors.neutral[200]}
    bg={isSelected ? colors.brand[50] : "white"}
    transition="all 0.15s"
    _hover={{ borderColor: colors.brand[600], bg: colors.brand[50] }}
  >
    <Flex align="center" justify="space-between" gap={3}>
      <Flex align="center" gap={3}>
        <Box
          w="20px" h="20px" borderRadius="full"
          border="2px solid"
          borderColor={isSelected ? colors.brand[600] : colors.neutral[400]}
          bg={isSelected ? colors.brand[600] : "white"}
          display="flex" alignItems="center" justifyContent="center"
          flexShrink={0}
        >
          {isSelected && <Box w="6px" h="6px" borderRadius="full" bg="white" />}
        </Box>
        <Box>
          <Flex align="center" gap={2}>
            <Icon as={option.id === "express" ? FiZap : FiTruck} color={isSelected ? colors.brand[600] : colors.neutral[500]} boxSize={3.5} />
            <Text fontSize="13px" fontWeight="800" color={colors.neutral[900]} m={0}>{option.label}</Text>
          </Flex>
          <Flex align="center" gap={1} mt="2px">
            <Icon as={FiClock} color={colors.neutral[400]} boxSize={3} />
            <Text fontSize="11px" color={colors.neutral[500]} m={0}>{option.etaLabel}</Text>
          </Flex>
          <Text fontSize="11px" color={colors.neutral[500]} mt="1px" m={0}>{option.description}</Text>
        </Box>
      </Flex>
      <Box textAlign="right" flexShrink={0}>
        <Text fontSize="14px" fontWeight="800" color={colors.neutral[900]} m={0}>{formatCurrency(option.deliveryFee)}</Text>
        <Text fontSize="10px" color={colors.neutral[400]} m={0}>{formatDistance(option.distanceKm)}</Text>
      </Box>
    </Flex>
  </Box>
);

export default function OrderCheckout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [cookies, , removeCookie] = useCookies(["token", "role"]);
  const isLoggedIn = Boolean(cookies.token || hasCookie("token"));
  const toast = useToast();

  const [username, setUsername] = useState("");
  const [fullUser, setFullUser] = useState(null);

  const state = location.state || {};
  const pharmacy = state.pharmacy || null;
  const cartItems = Array.isArray(state.cartItems) ? state.cartItems : [];
  const pharmacyItems = Array.isArray(state.pharmacyItems) ? state.pharmacyItems : [];
  const deliveryCoords = state.deliveryCoords || null;

  const [deliveryAddress, setDeliveryAddress] = useState(state.deliveryAddress || "");
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const [placing, setPlacing] = useState(false);

  const [prescriptionFiles, setPrescriptionFiles] = useState([]);
  const [savedPrescriptionRefs, setSavedPrescriptionRefs] = useState([]); // existing saved prescriptions (no re-upload)
  const [prescriptionHistory, setPrescriptionHistory] = useState([]);
  const [prescriptionHistoryLoading, setPrescriptionHistoryLoading] = useState(false);
  const [prescriptionHistoryLoaded, setPrescriptionHistoryLoaded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const prescriptionInputRef = useRef(null);

  useEffect(() => {
    const scanned = getScannedPrescriptionFile();
    if (scanned) {
      setPrescriptionFiles([scanned]);
      return;
    }
    const savedFiles = getPendingSavedPrescriptions();
    if (savedFiles?.length) {
      (async () => {
        const loaded = [];
        for (const f of savedFiles) {
          try {
            const url = resolveFileUrl(f.path);
            const resp = await fetch(url, { credentials: "include" });
            const blob = await resp.blob();
            loaded.push(new File([blob], f.originalName || f.fileName || "prescription", { type: f.mimeType || blob.type || "image/jpeg" }));
          } catch {}
        }
        clearPendingSavedPrescriptions();
        if (loaded.length) setPrescriptionFiles(loaded);
      })();
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchUser = async () => {
      try {
        const { data } = await axios.post(`${API_BASE}`, {}, { withCredentials: true });
        if (data?.status && data?.user) {
          setUsername(data.user);
          const fullUserData = await axios.get(`${API_BASE}/patient/myInfo/${data.user}`);
          setFullUser(fullUserData.data);
        }
      } catch {}
    };
    fetchUser();
  }, [isLoggedIn]);

  const hasRxMedicines = cartItems.some((ci) => {
    const v = ci.prescription_required;
    if (!v) return false;
    if (v === true) return true;
    const s = String(v).toLowerCase().trim();
    return s === "true" || s.includes("prescription");
  });

  const fetchPrescriptionHistory = useCallback(async () => {
    if (prescriptionHistoryLoaded) { setShowHistory(true); return; }
    setPrescriptionHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/order/prescription/history`, { withCredentials: true });
      setPrescriptionHistory(Array.isArray(data) ? data : []);
      setPrescriptionHistoryLoaded(true);
      setShowHistory(true);
    } catch (e) {
      setPrescriptionHistory([]);
    } finally {
      setPrescriptionHistoryLoading(false);
    }
  }, [prescriptionHistoryLoaded]);

  const handlePrescriptionFileChange = (fileList) => {
    const accepted = Array.from(fileList || []).filter((f) =>
      ["image/jpeg", "image/png", "application/pdf"].includes(f.type)
    );
    if (!accepted.length) {
      toast({ title: "Only JPG, PNG or PDF allowed", status: "warning", duration: 2200, isClosable: true, position: "bottom-right" });
      return;
    }
    setPrescriptionFiles((prev) => [...prev, ...accepted]);
    setShowHistory(false);
  };

  const handleUseSavedPrescription = (saved) => {
    setSavedPrescriptionRefs((prev) => {
      const exists = prev.some((r) => r.path === saved.path);
      if (exists) {
        toast({ title: "Already selected", status: "info", duration: 1600, isClosable: true, position: "bottom-right" });
        return prev;
      }
      toast({ title: "Prescription added", status: "success", duration: 1600, isClosable: true, position: "bottom-right" });
      return [...prev, { path: saved.path, originalName: saved.originalName || saved.fileName, fileName: saved.fileName, mimeType: saved.mimeType || "image/jpeg" }];
    });
    setShowHistory(false);
  };

  // Compute medicines subtotal: use store sale_rate when found, fallback to catalog price for not-found items
  const medicinesSubtotal = cartItems.reduce((sum, ci, i) => {
    const pi = pharmacyItems[i];
    const isNotFound = !pi || pi.reason === 'item_not_found' || Number(pi.sale_rate || 0) === 0;
    const linePrice = isNotFound ? Number(ci.price || 0) : Number(pi.sale_rate || 0);
    return sum + linePrice * Number(ci.quantity || 1);
  }, 0);

  // Original catalog subtotal (before Easysol prices)
  const originalSubtotal = cartItems.reduce((sum, ci) => {
    return sum + Number(ci.price || 0) * Number(ci.quantity || 1);
  }, 0);

  const deliveryFee = selectedOption ? Number(selectedOption.deliveryFee || 0) : 0;
  const orderTotal = medicinesSubtotal + deliveryFee;

  const fetchQuote = useCallback(async () => {
    if (!pharmacy?.address || !deliveryAddress) return;
    
    // Skip Borzo if pharmacy has its own delivery
    if (pharmacy?.delivery?.enabled) {
      return;
    }
    
    setQuoteLoading(true);
    setQuoteError("");
    try {
      const { data } = await axios.post(`${API}/order/delivery-options/quote`, {
        pickupAddress: pharmacy.address,
        deliveryAddress,
        pickupCoords: pharmacy.location?.coordinates
          ? { lat: pharmacy.location.coordinates[1], lng: pharmacy.location.coordinates[0] }
          : null,
        deliveryCoords: deliveryCoords
          ? { lat: deliveryCoords[1] ?? deliveryCoords.lat, lng: deliveryCoords[0] ?? deliveryCoords.lng }
          : null,
        orderSubtotal: medicinesSubtotal,
      }, { withCredentials: true });
      const opts = data.options || [];
      setDeliveryOptions(opts);
      if (opts.length) setSelectedOption(opts[0]);
    } catch (err) {
      setQuoteError("Could not load delivery options. A fallback estimate will apply.");
      const fallback = [
        { id: "same_day", label: "Same-day delivery", description: "Most economical option for today.", deliveryFee: 49, amount: medicinesSubtotal + 49, distanceKm: 0, etaLabel: "Delivered today", currency: "INR" },
        { id: "express", label: "Express delivery", description: "Priority pickup and faster dispatch.", deliveryFee: 79, amount: medicinesSubtotal + 79, distanceKm: 0, etaLabel: "Priority dispatch", currency: "INR" },
      ];
      setDeliveryOptions(fallback);
      setSelectedOption(fallback[0]);
    } finally {
      setQuoteLoading(false);
    }
  }, [pharmacy, deliveryAddress, deliveryCoords, medicinesSubtotal]);

  // Debug: log pharmacy data
  useEffect(() => {
    console.log("Pharmacy data:", pharmacy);
    console.log("Pharmacy delivery:", pharmacy?.delivery);
  }, [pharmacy]);

  // Add pharmacy's own delivery option if enabled, skip Borzo if pharmacy has delivery
  useEffect(() => {
    console.log("Checking pharmacy delivery:", pharmacy?.delivery?.enabled);
    if (pharmacy?.delivery?.enabled) {
      const pharmDelivery = pharmacy.delivery;
      const distance = pharmacy.distanceKm || 0;
      const isFree = pharmDelivery.isFree || (
        medicinesSubtotal >= pharmDelivery.freeMinAmount && 
        distance <= pharmDelivery.freeMaxDistanceKm
      );
      const fee = isFree ? 0 : pharmDelivery.fee;
      
      const pharmacyDeliveryOption = {
        id: "pharmacy_own",
        label: "Store delivery",
        description: isFree 
          ? `Free delivery (₹${pharmDelivery.freeMinAmount}+ within ${pharmDelivery.freeMaxDistanceKm}km)`
          : `Delivery by ${pharmacy.name}`,
        deliveryFee: fee,
        amount: medicinesSubtotal + fee,
        distanceKm: distance,
        etaLabel: "Delivered by store",
        currency: "INR",
        isFreeDelivery: isFree,
        provider: "pharmacy"
      };
      
      // Replace all options with only pharmacy delivery (skip Borzo)
      setDeliveryOptions([pharmacyDeliveryOption]);
      setSelectedOption(pharmacyDeliveryOption);
    }
  }, [pharmacy, medicinesSubtotal]);

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  // Redirect if landed without required state
  useEffect(() => {
    if (!pharmacy || !cartItems.length) {
      navigate("/home", { replace: true });
    }
  }, [pharmacy, cartItems.length, navigate]);

  const handleConfirmOrder = async () => {
    if (!selectedOption) {
      toast({ title: "Select a delivery option", status: "warning", duration: 2000, isClosable: true, position: "bottom-right" });
      return;
    }
    if (!deliveryAddress.trim()) {
      toast({ title: "Enter a delivery address", status: "warning", duration: 2000, isClosable: true, position: "bottom-right" });
      return;
    }
    if (hasRxMedicines && prescriptionFiles.length === 0 && savedPrescriptionRefs.length === 0) {
      toast({ title: "Prescription required", description: "One or more medicines require a prescription. Please upload before placing the order.", status: "warning", duration: 3500, isClosable: true, position: "bottom-right" });
      return;
    }
    setPlacing(true);
    try {
      const form = new FormData();
      form.append("pharmacyId", pharmacy.pharmacyId || pharmacy.id || "");
      form.append("pharmacyName", pharmacy.name || "");
      form.append("deliveryAddress", deliveryAddress || "");
      form.append("deliveryFullAddress", deliveryAddress || "");
      const coords = Array.isArray(deliveryCoords) ? deliveryCoords : null;
      form.append("deliveryLat", coords ? String(coords[1] ?? "") : "");
      form.append("deliveryLng", coords ? String(coords[0] ?? "") : "");
      form.append("selectedMedicines", JSON.stringify(cartItems.map((ci) => ({
        product_id: ci.productId || ci.product_id || "",
        product_name: ci.name || ci.product_name || "",
        manufacturer: ci.manufacturer || "",
        pack_size: ci.pack_size || "",
        composition: ci.composition || "",
        qty: ci.quantity || 1,
        mrp: ci.price || 0,
      }))));
      form.append("deliveryServiceLevel", selectedOption.id || "");
      form.append("deliveryServiceLabel", selectedOption.label || "");
      form.append("deliveryServiceFee", String(selectedOption.deliveryFee || 0));
      form.append("deliveryServiceAmount", String(selectedOption.amount || orderTotal));
      form.append("deliveryOriginalFee", String(selectedOption.originalDeliveryFee || selectedOption.deliveryFee || 0));
      form.append("deliveryConcessionAmount", String(selectedOption.concessionAmount || 0));
      form.append("deliveryConcessionLabel", selectedOption.concessionLabel || "");
      form.append("deliveryConcessionType", selectedOption.concessionType || "none");
      form.append("deliveryIsFree", String(Boolean(selectedOption.isFreeDelivery)));
      form.append("deliveryServiceCurrency", selectedOption.currency || "INR");
      form.append("deliveryDistanceKm", String(selectedOption.distanceKm || 0));
      form.append("deliveryEtaLabel", selectedOption.etaLabel || "");
      prescriptionFiles.forEach((f) => form.append("prescriptions", f));
      if (savedPrescriptionRefs.length > 0) {
        form.append("savedPrescriptionRefs", JSON.stringify(savedPrescriptionRefs));
      }

      const resp = await axios.post(`${API}/order/prescription`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const created = resp?.data;

      if (!created?._id) {
        throw new Error("Order creation failed");
      }

      // Clear the cart — order is placed, pharmacist will review before payment
      try {
        await axios.delete(`${API}/cart/clear`, { withCredentials: true });
      } catch (_) {}
      clearScannedPrescriptionFile();

      toast({
        title: "Order sent to pharmacy",
        description: "The pharmacist is reviewing your order. You'll be notified when it's ready for payment.",
        status: "success",
        duration: 3500,
        isClosable: true,
        position: "bottom-right",
      });

      navigate("/order/confirmed", {
        replace: true,
        state: { order: created, pharmacy, medicines: cartItems, pharmacistReviewing: true },
      });
    } catch (err) {
      toast({
        title: "Could not place order",
        description: err?.response?.data?.message || "Please try again.",
        status: "error",
        duration: 3500,
        isClosable: true,
        position: "bottom-right",
      });
      setPlacing(false);
    }
  };

  if (!pharmacy || !cartItems.length) return null;

  const logout = () => performLogout(removeCookie);

  // Merge pharmacyItems with cartItems for display
  // Always use catalog name; use store sale_rate when found, fallback to catalog price for not-found
  const displayItems = cartItems.map((ci, i) => {
    const pi = pharmacyItems[i];
    const isNotFound = !pi || pi.reason === 'item_not_found' || Number(pi.sale_rate || 0) === 0;
    const storeName = pi?.store_name || pi?.item_name || null;
    // Get original price from cart item (could be price or mrp)
    const originalPrice = Number(ci.price || ci.mrp || 0);
    const estimatedPrice = isNotFound ? originalPrice : Number(pi.sale_rate || 0);
    return {
      name: ci.name || ci.product_name || "Medicine",
      storeName: isNotFound ? null : storeName,
      composition: ci.composition || null,
      packSize: ci.pack_size || null,
      quantity: Number(ci.quantity || 1),
      originalPrice,
      estimatedPrice,
      saleRate: estimatedPrice,
      mrp: Number(pi?.mrp || 0),
      available: pi ? (pi.is_available ?? true) : null,
      isNotFound,
      mrpMismatch: pi?.mrp_mismatch || false,
      mrpMismatchDetail: pi?.mrp_mismatch_detail || null,
    };
  });

  return (
    <Box minH="100vh" bg={colors.neutral[50]} overflowX="hidden" maxW="100vw">
      <WithSubnavigation
        username={isLoggedIn ? username : ""}
        name={isLoggedIn ? fullUser?.Name : ""}
        isLoggedIn={isLoggedIn}
        cartCount={cartItems.length}
        sidebarOffset="0px"
        onLogout={logout}
      />

      <Box maxW="1100px" mx="auto" px={{ base: 4, md: 6, lg: 8 }} pt={4} pb={6} overflowX="hidden">
        {/* Back + heading inline */}
        <Flex align="center" gap={3} mb={4}>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<FiArrowLeft />}
            color={colors.neutral[500]}
            onClick={() => navigate("/home")}
            _hover={{ color: colors.neutral[900], bg: colors.neutral[100] }}
            flexShrink={0}
          >
            Back
          </Button>
          <Box>
            <Text fontWeight="900" fontSize="18px" color={colors.neutral[900]} letterSpacing="-0.02em" m={0}>
              Review &amp; Confirm Order
            </Text>
            <Text fontSize="12px" color={colors.neutral[500]} m={0}>
              Check medicines, choose delivery, then confirm.
            </Text>
          </Box>
        </Flex>

        {/* Two-column layout */}
        <Flex gap={6} align="flex-start" direction={{ base: "column", lg: "row" }}>

          {/* LEFT — Pharmacy + Medicines */}
          <Stack spacing={4} flex={1} minW={0}>

            {/* Pharmacy */}
            <SectionCard>
              <SectionHeader icon={FiMapPin} title="Pharmacy" subtitle="Your selected store" />
              <Box px={5} py={4}>
                <Flex align="flex-start" justify="space-between" gap={3} wrap="wrap">
                  <Box>
                    <Text fontWeight="800" fontSize="14px" color={colors.neutral[900]} m={0}>{pharmacy.name}</Text>
                    {pharmacy.address && (
                      <Flex align="center" gap={1} mt={1}>
                        <Icon as={FiMapPin} color={colors.neutral[400]} boxSize={3} />
                        <Text fontSize="12px" color={colors.neutral[500]} m={0}>{pharmacy.address}</Text>
                      </Flex>
                    )}
                  </Box>
                  <Tag borderRadius="full" bg="#f0fdfa" color="#0f766e" fontSize="11px" fontWeight="700" px={3}>
                    <Icon as={FiCheckCircle} boxSize={3} mr={1} />
                    Selected
                  </Tag>
                </Flex>
              </Box>
            </SectionCard>

            {/* Medicines */}
            <SectionCard>
              <SectionHeader icon={FiPackage} title="Medicines" subtitle={`${displayItems.length} item${displayItems.length !== 1 ? "s" : ""} · read-only`} />
              <Box px={5} py={2} bg="#fffbeb" borderBottom="1px solid #fde68a">
                <HStack spacing={2}>
                  <Icon as={FiInfo} boxSize={4} color="#d97706" />
                  <Text fontSize="11px" color="#b45309" m={0} fontWeight="600">
                    Prices and availability are estimates. Final price and stock will be confirmed by the pharmacy before processing your order.
                  </Text>
                </HStack>
              </Box>
              <Box px={5} py={3}>
                <Stack spacing={0}>
                  {displayItems.map((item, i) => {
                    const originalLineTotal = item.originalPrice * item.quantity;
                    const estimatedLineTotal = item.estimatedPrice * item.quantity;
                    const priceDiffers = Math.abs(item.estimatedPrice - item.originalPrice) > 0.01;
                    return (
                      <Flex
                        key={i}
                        align="center"
                        justify="space-between"
                        gap={3}
                        py={2.5}
                        borderBottom={i < displayItems.length - 1 ? "1px solid" : "none"}
                        borderColor={colors.neutral[100]}
                      >
                        <Flex align="center" gap={2} minW={0} flex={1}>
                          <Box w="6px" h="6px" borderRadius="full" flexShrink={0}
                            bg={item.available === null ? colors.neutral[300] : item.available ? "#22c55e" : "#ef4444"}
                          />
                          <Box minW={0}>
                            <Text fontSize="13px" fontWeight="700" color={colors.neutral[800]} noOfLines={1} m={0}>
                              {item.name}
                              {item.storeName && (
                                <Text as="span" fontSize="11px" color={colors.brand[600]} fontWeight="600"> → {item.storeName}</Text>
                              )}
                            </Text>
                            <Text fontSize="11px" color={colors.neutral[500]} m={0}>Qty: {item.quantity}{item.packSize ? ` · ${item.packSize}` : ''}</Text>
                            {item.composition && (
                              <Text fontSize="10px" color={colors.neutral[400]} noOfLines={1} m={0}>{item.composition}</Text>
                            )}
                            {item.mrpMismatch && (
                              <Text fontSize="10px" color="#b45309" m={0}>⚠ MRP differs: {item.mrpMismatchDetail}</Text>
                            )}
                          </Box>
                        </Flex>
                        <Box textAlign="right" flexShrink={0}>
                          {/* Show original price */}
                          {priceDiffers ? (
                            <>
                              <Text fontSize="10px" color={colors.neutral[400]} textDecoration="line-through" m={0}>
                                {formatCurrencyPrecise(originalLineTotal)}
                              </Text>
                              <Text fontSize="13px" fontWeight="700" color={colors.brand[600]} m={0}>
                                {formatCurrencyPrecise(estimatedLineTotal)}
                              </Text>
                              <Text fontSize="9px" color={colors.brand[500]} fontWeight="600" m={0}>Est. price</Text>
                            </>
                          ) : (
                            <Text fontSize="13px" fontWeight="700" color={colors.brand[600]} m={0}>
                              {formatCurrencyPrecise(originalLineTotal)}
                            </Text>
                          )}
                        </Box>
                      </Flex>
                    );
                  })}
                </Stack>
              </Box>
              <Flex px={5} py={3} bg={colors.neutral[50]} borderTop="1px solid" borderColor={colors.neutral[100]} justify="space-between" align="center" direction="column" alignItems="flex-start" gap={1}>
                <Flex w="full" justify="space-between" align="center">
                  <Text fontSize="12px" fontWeight="700" color={colors.neutral[500]} m={0} textTransform="uppercase" letterSpacing="0.06em">
                    Medicines (catalog)
                  </Text>
                  <Text fontSize="14px" fontWeight="800" color={colors.neutral[400]} m={0} textDecoration={Math.abs(medicinesSubtotal - originalSubtotal) > 0.01 ? "line-through" : "none"}>
                    {formatCurrency(originalSubtotal)}
                  </Text>
                </Flex>
                {Math.abs(medicinesSubtotal - originalSubtotal) > 0.01 && (
                  <Flex w="full" justify="space-between" align="center">
                    <Text fontSize="12px" fontWeight="700" color={colors.brand[600]} m={0} textTransform="uppercase" letterSpacing="0.06em">
                      Medicines (estimated)
                    </Text>
                    <Text fontSize="14px" fontWeight="800" color={colors.brand[600]} m={0}>
                      {formatCurrency(medicinesSubtotal)}
                    </Text>
                  </Flex>
                )}
              </Flex>
            </SectionCard>

            {/* Prescription upload */}
            <SectionCard border={hasRxMedicines && prescriptionFiles.length === 0 && savedPrescriptionRefs.length === 0 ? "1.5px solid #fca5a5" : undefined}>
              <SectionHeader
                icon={hasRxMedicines && prescriptionFiles.length === 0 && savedPrescriptionRefs.length === 0 ? FiAlertTriangle : FiFileText}
                title="Prescription"
                subtitle={
                  hasRxMedicines && prescriptionFiles.length === 0 && savedPrescriptionRefs.length === 0
                    ? "Required — medicines in your cart need an Rx"
                    : (prescriptionFiles.length + savedPrescriptionRefs.length)
                    ? `${prescriptionFiles.length + savedPrescriptionRefs.length} file${(prescriptionFiles.length + savedPrescriptionRefs.length) > 1 ? "s" : ""} selected`
                    : "Optional — attach if available"
                }
              />
              <Box px={5} py={4}>
                <input
                  ref={prescriptionInputRef}
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => handlePrescriptionFileChange(e.target.files)}
                />

                {(prescriptionFiles.length > 0 || savedPrescriptionRefs.length > 0) && (
                  <Stack spacing={1.5} mb={3}>
                    {savedPrescriptionRefs.map((r, i) => (
                      <Flex key={`saved-${i}`} align="center" justify="space-between" px={3} py={2} border="1px solid #e2e8f0" borderRadius="10px" bg="#f0fdf4">
                        <HStack spacing={2} minW={0} flex={1}>
                          <Icon as={FiFileText} color="#0f766e" boxSize={3.5} flexShrink={0} />
                          <Text fontSize="12px" fontWeight="600" color="#0f172a" noOfLines={1} m={0}>{r.originalName}</Text>
                          <Text fontSize="10px" color="#0f766e" m={0} fontWeight="600">saved</Text>
                        </HStack>
                        <Button size="xs" variant="ghost" color="#94a3b8" onClick={() => setSavedPrescriptionRefs((prev) => prev.filter((_, j) => j !== i))} px={1} ml={2}>✕</Button>
                      </Flex>
                    ))}
                    {prescriptionFiles.map((f, i) => (
                      <Flex key={`new-${i}`} align="center" justify="space-between" px={3} py={2} border="1px solid #e2e8f0" borderRadius="10px" bg="#f8fafc">
                        <HStack spacing={2} minW={0} flex={1}>
                          <Icon as={f.type.startsWith("image/") ? FiFileText : FiFile} color="#0f766e" boxSize={3.5} flexShrink={0} />
                          <Text fontSize="12px" fontWeight="600" color="#0f172a" noOfLines={1} m={0}>{f.name}</Text>
                        </HStack>
                        <Button size="xs" variant="ghost" color="#94a3b8" onClick={() => setPrescriptionFiles((prev) => prev.filter((_, j) => j !== i))} px={1} ml={2}>✕</Button>
                      </Flex>
                    ))}
                  </Stack>
                )}

                <Stack spacing={2}>
                  <Button
                    size="sm" h="36px" w="full"
                    bg={hasRxMedicines && prescriptionFiles.length === 0 && savedPrescriptionRefs.length === 0 ? "linear-gradient(135deg,#dc2626 0%,#ef4444 100%)" : "linear-gradient(135deg,#0f766e 0%,#0d9488 100%)"}
                    color="white"
                    _hover={{ opacity: 0.9, transform: "translateY(-1px)" }}
                    borderRadius="10px"
                    leftIcon={<FiUploadCloud size={14} />}
                    fontWeight="700" fontSize="sm"
                    onClick={() => prescriptionInputRef.current?.click()}
                  >
                    {prescriptionFiles.length ? "Add another file" : "Upload prescription"}
                  </Button>
                  <Button
                    size="sm" h="32px" w="full"
                    variant="outline" borderColor="#e2e8f0"
                    color="#475569" borderRadius="10px"
                    fontWeight="700" fontSize="xs"
                    isLoading={prescriptionHistoryLoading}
                    onClick={fetchPrescriptionHistory}
                  >
                    Use saved prescription
                  </Button>
                </Stack>

                {showHistory && (
                  <Box mt={3} border="1px solid #e2e8f0" borderRadius="12px" overflow="hidden">
                    <Flex align="center" justify="space-between" px={3} py={2} bg="#f8fafc" borderBottom="1px solid #e2e8f0">
                      <Text fontSize="11px" fontWeight="700" color="#64748b" textTransform="uppercase" letterSpacing="0.06em" m={0}>Saved prescriptions</Text>
                      <Button size="xs" variant="ghost" color="#94a3b8" onClick={() => setShowHistory(false)} px={1} h={5} minW={0} fontSize="11px">✕</Button>
                    </Flex>
                    {prescriptionHistory.length === 0 ? (
                      <Box px={3} py={4} textAlign="center">
                        <Text fontSize="xs" color="#94a3b8" m={0}>No previous prescriptions found</Text>
                      </Box>
                    ) : (
                      <Stack spacing={0} divider={<Box h="1px" bg="#f1f5f9" />} maxH="180px" overflowY="auto">
                        {[...new Map(prescriptionHistory.map((p) => [p.path, p])).values()].map((p, idx) => (
                          <Flex key={p.path || idx} align="center" justify="space-between" px={3} py={2.5}
                            _hover={{ bg: "#f0fdfa" }} cursor="pointer"
                            onClick={() => handleUseSavedPrescription(p)}
                          >
                            <HStack spacing={2} minW={0} flex={1}>
                              <Icon as={FiFileText} color="#0f766e" boxSize={3.5} flexShrink={0} />
                              <Box minW={0}>
                                <Text fontSize="12px" fontWeight="600" color="#0f172a" noOfLines={1} m={0}>{p.originalName || p.fileName}</Text>
                                <Text fontSize="10px" color="#94a3b8" m={0}>
                                  {p.uploadedAt ? new Date(p.uploadedAt).toLocaleDateString() : ""}
                                  {p.orderStatus ? ` · ${p.orderStatus}` : ""}
                                </Text>
                              </Box>
                            </HStack>
                            <Box as="span" fontSize="11px" fontWeight="700" color="#0f766e" bg="#f0fdfa" px={2} py="2px" borderRadius="full" flexShrink={0}>Use</Box>
                          </Flex>
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}
              </Box>
            </SectionCard>

          </Stack>

          {/* RIGHT — Delivery + Summary + Confirm (sticky on desktop) */}
          <Box w={{ base: "full", lg: "360px" }} flexShrink={0}>
            <Box position={{ base: "static", lg: "sticky" }} top="90px">
              <Stack spacing={4}>

                {/* Delivery */}
                <SectionCard>
                  <SectionHeader icon={FiTruck} title="Delivery" subtitle="Choose speed &amp; confirm address" />
                  <Box px={4} pt={4} pb={4}>
                    {/* Delivery address */}
                    <Box mb={3}>
                      <Text fontSize="11px" fontWeight="700" color={colors.neutral[500]} textTransform="uppercase" letterSpacing="0.06em" mb={1}>Deliver to</Text>
                      <Input
                        value={deliveryAddress}
                        onChange={(e) => setDeliveryAddress(e.target.value)}
                        placeholder="Enter your delivery address"
                        size="sm"
                        borderRadius="8px"
                        borderColor={deliveryAddress.trim() ? colors.neutral[300] : "#fca5a5"}
                        _focus={{ borderColor: colors.brand[500], boxShadow: `0 0 0 1px ${colors.brand[500]}` }}
                        fontSize="13px"
                      />
                    </Box>
                    {quoteLoading ? (
                      <Flex align="center" gap={3} py={3}>
                        <Spinner size="sm" color={colors.brand[500]} />
                        <Text fontSize="13px" color={colors.neutral[500]} m={0}>Calculating cost…</Text>
                      </Flex>
                    ) : (
                      <Stack spacing={3}>
                        {quoteError && (
                          <Flex align="center" gap={2} bg="#fffbeb" px={3} py={2} borderRadius="8px">
                            <Icon as={FiAlertCircle} color="#b45309" boxSize={3.5} />
                            <Text fontSize="11px" color="#b45309" m={0}>{quoteError}</Text>
                          </Flex>
                        )}
                        {deliveryOptions.map((opt) => (
                          <DeliveryOptionCard
                            key={opt.id}
                            option={opt}
                            isSelected={selectedOption?.id === opt.id}
                            onSelect={setSelectedOption}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                </SectionCard>

                {/* Order Summary */}
                <SectionCard>
                  <SectionHeader icon={FiCheck} title="Order Summary" />
                  <Box px={5} py={4}>
                    <Stack spacing={2}>
                      <Flex justify="space-between">
                        <Text fontSize="13px" color={colors.neutral[500]} m={0}>Medicines</Text>
                        <Stack spacing={0} align="flex-end">
                          {Math.abs(medicinesSubtotal - originalSubtotal) > 0.01 && (
                            <Text fontSize="11px" color={colors.neutral[400]} textDecoration="line-through" m={0}>{formatCurrency(originalSubtotal)}</Text>
                          )}
                          <Text fontSize="13px" fontWeight="700" color={Math.abs(medicinesSubtotal - originalSubtotal) > 0.01 ? colors.brand[600] : colors.neutral[800]} m={0}>
                            {formatCurrency(medicinesSubtotal)}
                            {Math.abs(medicinesSubtotal - originalSubtotal) > 0.01 && (
                              <Text as="span" fontSize="9px" color={colors.brand[500]} ml={1}>(est.)</Text>
                            )}
                          </Text>
                        </Stack>
                      </Flex>
                      <Flex justify="space-between">
                        <Text fontSize="13px" color={colors.neutral[500]} m={0}>
                          Delivery
                          {selectedOption && <Text as="span" color={colors.neutral[400]} fontSize="11px"> ({selectedOption.label})</Text>}
                        </Text>
                        <Text fontSize="13px" fontWeight="700" color={colors.neutral[800]} m={0}>
                          {quoteLoading ? "…" : formatCurrency(deliveryFee)}
                        </Text>
                      </Flex>
                      <Divider borderColor={colors.neutral[200]} />
                      <Flex justify="space-between" align="center">
                        <Text fontSize="14px" fontWeight="800" color={colors.neutral[900]} m={0}>Total payable</Text>
                        <Text fontSize="20px" fontWeight="900" color={colors.brand[600]} m={0}>{formatCurrency(orderTotal)}</Text>
                      </Flex>
                    </Stack>
                  </Box>
                </SectionCard>

                {/* Confirm */}
                <Button
                  size="lg"
                  h="52px"
                  w="full"
                  bg={selectedOption ? "linear-gradient(135deg, #0f766e 0%, #0d9488 100%)" : colors.neutral[200]}
                  color={selectedOption ? "white" : colors.neutral[400]}
                  _hover={selectedOption ? {
                    bg: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 8px 24px rgba(15,118,110,0.35)",
                  } : {}}
                  _active={{ transform: "none" }}
                  borderRadius="14px"
                  fontWeight="800"
                  fontSize="15px"
                  isLoading={placing}
                  isDisabled={!selectedOption || quoteLoading}
                  onClick={handleConfirmOrder}
                >
                  Place Order — {formatCurrency(orderTotal)}
                </Button>

              </Stack>
            </Box>
          </Box>

        </Flex>
      </Box>
    </Box>
  );
}
